import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-secondary font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-heritage-blue text-white shadow-xs hover:bg-heritage-blue/90 focus-visible:ring-heritage-blue/20",
        destructive:
          "bg-secondary-red text-white shadow-xs hover:bg-secondary-red/90 focus-visible:ring-secondary-red/20",
        outline:
          "border border-heritage-blue bg-background text-heritage-blue shadow-xs hover:bg-heritage-blue hover:text-white",
        secondary:
          "bg-future-blue text-white shadow-xs hover:bg-future-blue/80 focus-visible:ring-future-blue/20",
        ghost:
          "text-heritage-blue hover:bg-heritage-blue/10 hover:text-heritage-blue",
        link: "text-future-blue underline-offset-4 hover:underline hover:text-future-blue/80",
        // Strategic pillar variants
        scale: "bg-scale-500 text-white shadow-xs hover:bg-scale-600",
        quality: "bg-quality-500 text-white shadow-xs hover:bg-quality-600", 
        culture: "bg-culture-500 text-white shadow-xs hover:bg-culture-600",
        impact: "bg-impact-500 text-white shadow-xs hover:bg-impact-600",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean
    }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  )
})
Button.displayName = "Button"

export { Button, buttonVariants }
